/**
 * Knowledge Base Search Utility
 *
 * Performs SEMANTIC search against the Dubai Police emergency knowledge base
 * by calling the backend's POST /api/knowledge-base/search endpoint.
 *
 * The backend embeds the query with Gemini and returns the top matching Q&A
 * entries from Q&As.json — these are then passed back to the Gemini Live API
 * as the search_knowledge_base tool result, completing the RAG loop:
 *
 *   User speaks → LLM calls search_knowledge_base tool
 *   → this utility → backend semantic search
 *   → top Q&As returned to LLM → LLM answers using retrieved context
 */

import { config } from '../config/runtime-config';

export interface KBResult {
  faq_id: string;
  language: string;
  question: string;
  answer: string;
  category: string;
  similarity_score: number;
  keywords: string[];
}

export interface KBSearchResponse {
  results: KBResult[];
  count: number;
}

function getBackendUrl(): string {
  return config.REACT_APP_BACKEND_URL || 'http://localhost:8000';
}

/**
 * Search the knowledge base using backend semantic (vector) search.
 *
 * @param query    - The user's question
 * @param topK     - Max results to return (default 5)
 * @param language - Optional language filter: "ar" | "en" | "ur"
 * @returns        Array of matching KB entries sorted by similarity score
 */
export async function searchKnowledgeBase(
  query: string,
  topK: number = 5,
  language?: string,
): Promise<KBResult[]> {
  if (!query || !query.trim()) return [];

  try {
    const backendUrl = getBackendUrl();
    const response = await fetch(`${backendUrl}/api/knowledge-base/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query.trim(),
        top_k: topK,
        language: language || null,
      }),
    });

    if (!response.ok) {
      console.warn(`[KB-Search] Backend returned ${response.status}`);
      return [];
    }

    const data: KBSearchResponse = await response.json();
    console.log(`[KB-Search] "${query.slice(0, 60)}" → ${data.count} results`);
    return data.results || [];
  } catch (error) {
    console.error('[KB-Search] Search request failed:', error);
    return [];
  }
}

/**
 * Format knowledge base results into a text block that the Gemini Live API
 * can consume as the tool result for search_knowledge_base.
 *
 * The LLM reads this text and uses it to answer the user's question.
 *
 * @param results  - Array of KBResult from searchKnowledgeBase()
 * @param language - User's language ("ar" | "en" | "ur")
 * @returns        Formatted multi-line string for the LLM
 */
export function formatKBResults(
  results: KBResult[],
  language: 'ar' | 'en' | 'ur' = 'en',
): string {
  if (results.length === 0) {
    const noResults: Record<string, string> = {
      ar: 'لم يُعثر على نتائج ذات صلة في قاعدة المعرفة.',
      en: 'No relevant entries found in the knowledge base.',
      ur: 'علم کی بنیاد میں کوئی متعلقہ نتائج نہیں ملے۔',
    };
    return noResults[language] || noResults.en;
  }

  const headers: Record<string, string> = {
    ar: `وُجدت ${results.length} نتيجة ذات صلة من قاعدة المعرفة:\n`,
    en: `Found ${results.length} relevant knowledge base entry(ies):\n`,
    ur: `علم کی بنیاد سے ${results.length} متعلقہ نتائج ملے:\n`,
  };

  const header = headers[language] || headers.en;

  const entries = results.map((r, i) => {
    const score = Math.round(r.similarity_score * 100);
    return (
      `Entry #${i + 1} [ID: ${r.faq_id}, Topic: ${r.category}, Match: ${score}%]\n` +
      `Q: ${r.question}\n` +
      `A: ${r.answer}`
    );
  });

  return header + entries.join('\n\n');
}

/**
 * Combined helper: search + format in one call.
 * Returns the formatted string ready to be returned as a tool result.
 */
export async function searchAndFormat(
  query: string,
  language: 'ar' | 'en' | 'ur' = 'en',
  topK: number = 5,
): Promise<string> {
  const results = await searchKnowledgeBase(query, topK, language);
  return formatKBResults(results, language);
}

// ---------------------------------------------------------------------------
// Backward-compat aliases (so old imports from faq-search.ts still work)
// ---------------------------------------------------------------------------

export type SearchResult = { faq: KBResult; relevanceScore: number; matchedKeywords: string[] };

export async function searchFAQs(
  query: string,
  limit: number = 3,
): Promise<SearchResult[]> {
  const results = await searchKnowledgeBase(query, limit);
  return results.map((r) => ({
    faq: r,
    relevanceScore: r.similarity_score,
    matchedKeywords: r.keywords || [],
  }));
}

export function formatSearchResults(
  results: SearchResult[],
  language: 'ar' | 'en' | 'ur' = 'en',
): string {
  return formatKBResults(
    results.map((r) => r.faq),
    language,
  );
}
